import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import PricingSection from "../components/PricingSection";
import PromoBar from "../components/PromoBar";
import ShowcaseSection from "../components/ShowcaseSection";

const LandingPage: React.FC = () => {
  const { scrollYProgress } = useScroll({ layoutEffect: false });
  const heroRef = useRef(null);
  const heroY = useTransform(scrollYProgress, [0, 0.5], [0, -80]);
  const [webFailed, setWebFailed] = useState(false);

  useMotionValueEvent(scrollYProgress, "change", () => {});

  useEffect(() => {
    const timer = setTimeout(() => {
      if (document.readyState === "complete") {
        window.dispatchEvent(new Event("load"));
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* ── Promo bar (outside the overflow-hidden wrapper so it sticks) ── */}
      <PromoBar />

      <div className="min-h-screen bg-white overflow-hidden">
        {/* ── Hero ── */}
        <header className="relative bg-gradient-to-b from-[#5bb8ff] via-[#4f8ef7] to-indigo-600">
          {/* Soft cloud blooms */}
          <div className="pointer-events-none absolute inset-0 -z-0 overflow-hidden">
            <div className="absolute -top-10 left-1/4 h-72 w-[28rem] -translate-x-1/2 rounded-full bg-white/30 blur-3xl"></div>
            <div className="absolute top-20 right-0 h-72 w-96 rounded-full bg-white/20 blur-3xl"></div>
            <div className="absolute bottom-10 left-0 h-64 w-80 rounded-full bg-white/15 blur-3xl"></div>
          </div>

          <div className="container relative z-10 mx-auto px-5 md:px-8 pt-12 pb-0 md:pt-16 max-w-6xl">
            {/* Eyebrow — social proof */}
            <motion.div
              className="mb-6 flex justify-center"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2.5 rounded-full bg-white/15 py-1.5 pl-2 pr-4 ring-1 ring-white/30 backdrop-blur">
                <span className="flex -space-x-2">
                  <span className="h-6 w-6 rounded-full border-2 border-white/80 bg-gradient-to-br from-sky-300 to-indigo-400" />
                  <span className="h-6 w-6 rounded-full border-2 border-white/80 bg-gradient-to-br from-indigo-300 to-purple-400" />
                  <span className="h-6 w-6 rounded-full border-2 border-white/80 bg-gradient-to-br from-amber-300 to-orange-400" />
                </span>
                <span className="flex items-center gap-1.5 text-xs font-semibold text-white">
                  <span className="text-amber-300 tracking-tight">
                    <i className="fas fa-star text-[10px]" />
                    <i className="fas fa-star text-[10px]" />
                    <i className="fas fa-star text-[10px]" />
                    <i className="fas fa-star text-[10px]" />
                    <i className="fas fa-star text-[10px]" />
                  </span>
                  Loved by 10,000+ learners
                </span>
              </div>
            </motion.div>

            {/* Title */}
            <div className="mb-7 flex flex-col items-center text-center md:mb-9">
              <motion.h1
                className="text-4xl font-extrabold leading-[1.05] tracking-tight text-white md:text-6xl"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
              >
                Everything you need to
              </motion.h1>
              <motion.p
                className="text-white"
                style={{
                  fontFamily: "'Caveat', cursive",
                  fontSize: "clamp(3rem, 9vw, 5.5rem)",
                  lineHeight: 1,
                }}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.25 }}
              >
                learn smarter
              </motion.p>
              <motion.p
                className="mt-5 max-w-lg text-base text-blue-50 md:text-lg"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35 }}
              >
                Understand any subject faster — and actually remember it.
              </motion.p>
            </div>

            {/* CTAs */}
            <motion.div
              className="mb-4 flex flex-col items-center justify-center gap-4 sm:flex-row"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
            >
              <a
                href="https://apps.apple.com/us/app/einstein-ai-learn-memorize/id6741355940?itscg=30200&itsct=apps_box_badge&mttnsubad=6741355940"
                target="_blank"
                rel="noopener noreferrer"
                className="transform transition-transform duration-300 hover:scale-105"
              >
                <img
                  src="https://toolbox.marketingtools.apple.com/api/v2/badges/download-on-the-app-store/black/en-us?releaseDate=1739404800"
                  alt="Download on the App Store"
                  className="h-[56px] w-auto"
                />
              </a>

              <Link
                to="/login"
                className="flex h-[56px] w-[200px] transform items-center justify-center gap-2.5 rounded-xl bg-white text-slate-900 shadow-lg transition-all duration-300 hover:scale-105 hover:bg-slate-50"
              >
                <i className="fas fa-globe text-2xl text-[#47b3fe]"></i>
                <span className="flex flex-col items-start leading-none">
                  <span className="text-[11px] font-medium text-slate-500">
                    Continue on the
                  </span>
                  <span className="text-lg font-bold tracking-tight">
                    Website
                  </span>
                </span>
              </Link>
            </motion.div>

            {/* Hero visual — huge cross-platform composition */}
            <motion.div
              className="relative mx-auto -mb-20 mt-10 max-w-6xl md:-mb-32 md:mt-14"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.4,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {/* Web browser window */}
              <div className="relative mx-auto w-[96%] max-w-6xl overflow-hidden rounded-2xl bg-white shadow-[0_50px_90px_-25px_rgba(15,23,42,0.55)] md:w-[94%]">
                <div className="flex items-center gap-1.5 bg-slate-100 px-4 py-3">
                  <span className="h-3 w-3 rounded-full bg-red-400" />
                  <span className="h-3 w-3 rounded-full bg-amber-400" />
                  <span className="h-3 w-3 rounded-full bg-green-400" />
                  <span className="ml-3 hidden h-5 max-w-xs flex-1 rounded-md bg-white sm:block" />
                </div>
                <div className="relative aspect-[1907/1080] bg-gradient-to-br from-blue-50 via-white to-indigo-50">
                  {!webFailed ? (
                    <img
                      src="/bezel/web.png"
                      alt="Einstein AI on the web"
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={() => setWebFailed(true)}
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-slate-300">
                      <i className="fas fa-desktop text-4xl" />
                      <span className="text-sm font-medium">
                        Web app preview
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Phone overlapping front-right */}
              <img
                src="/bezel/4.png"
                alt="Einstein AI app"
                className="absolute -bottom-4 right-0 z-10 w-[28%] max-w-[240px] drop-shadow-[0_30px_45px_rgba(15,23,42,0.5)] sm:right-2 md:right-6"
              />
            </motion.div>
          </div>

          {/* Smooth blend into the next section */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-64 bg-gradient-to-b from-transparent to-[#eef2f7]" />
        </header>

        {/* ── Features / cross-platform showcase ── */}
        <ShowcaseSection />

        {/* ── Pricing ── */}
        <PricingSection />

        {/* ── Social Proof ── */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#47b3fe]/10 rounded-full filter blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/10 rounded-full filter blur-3xl"></div>
          </div>

          <div className="container mx-auto px-5 md:px-8 max-w-5xl text-center relative z-10">
            <motion.h2
              className="text-3xl md:text-4xl font-bold mb-10 text-white tracking-tight"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Trusted by Students From
            </motion.h2>

            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-12"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {[
                { src: "/logos/harvard.png", alt: "Harvard University" },
                { src: "/logos/stanford.webp", alt: "Stanford University" },
                { src: "/logos/mit.png", alt: "MIT" },
                { src: "/logos/yale.png", alt: "Yale University" },
              ].map((logo, i) => (
                <div key={i} className="flex items-center justify-center">
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className="h-20 md:h-24 w-auto object-contain opacity-70 brightness-0 invert transition-all duration-300 hover:opacity-100 hover:brightness-100 hover:invert-0"
                  />
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {(() => {
                const isMobile =
                  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                    navigator.userAgent,
                  );

                return isMobile ? (
                  <a
                    href="https://apps.apple.com/us/app/einstein-ai-learn-memorize/id6741355940"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-7 py-3 bg-white hover:bg-blue-50 text-slate-900 text-sm font-semibold rounded-xl transition-colors duration-200 shadow-sm"
                  >
                    Join them today
                    <i className="fas fa-arrow-right text-xs"></i>
                  </a>
                ) : (
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 px-7 py-3 bg-white hover:bg-blue-50 text-slate-900 text-sm font-semibold rounded-xl transition-colors duration-200 shadow-sm"
                  >
                    Join them today
                    <i className="fas fa-arrow-right text-xs"></i>
                  </Link>
                );
              })()}
            </motion.div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="bg-white border-t border-slate-100 py-7 md:py-8">
          <div className="container mx-auto px-5 md:px-8 max-w-6xl">
            <div className="flex flex-col items-center gap-5 md:flex-row md:justify-between md:gap-0">
              <div className="flex items-center gap-2.5">
                <img
                  src="/a.png"
                  alt="Einstein AI Logo"
                  className="w-6 h-6 object-contain"
                />
                <p className="text-sm text-slate-500">
                  {new Date().getFullYear()} © Einstein AI. All rights reserved.
                </p>
              </div>
              <div className="flex items-center gap-6">
                <Link
                  to="/privacy"
                  className="text-sm text-slate-500 hover:text-slate-900 transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="text-sm text-slate-500 hover:text-slate-900 transition-colors"
                >
                  Terms of Service
                </Link>
                <a
                  href="mailto:catyardapp@gmail.com"
                  className="text-sm text-slate-500 hover:text-slate-900 transition-colors"
                >
                  Contact
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
